import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, Send, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { useAuthStore } from '../../../store/authStore';
import { useFeedbacks, useNotifications, useUsers } from '../../../hooks/useDatabase';
import { toast } from 'sonner';

export default function AdminFeedbackPage() {
  const { user } = useAuthStore();
  const { getUsers } = useUsers();
  const { getFeedbacks, createFeedback, loading: feedbackLoading } = useFeedbacks();
  const { createNotification } = useNotifications();

  const [users, setUsers] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [recipientRole, setRecipientRole] = useState('employee');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sendText, setSendText] = useState('');
  const [sendType, setSendType] = useState('general');
  const [selectedRecipient, setSelectedRecipient] = useState('');

  useEffect(() => {
    (async () => {
      const list = await getUsers();
      setUsers(list);
    })();
  }, [getUsers]);

  const loadFeedbacks = useMemo(() => async () => {
    const all = await getFeedbacks();
    setFeedbacks(all.filter(f => f.receiverRole === 'admin'));
  }, [getFeedbacks]);

  useEffect(() => {
    loadFeedbacks();
  }, [loadFeedbacks]);

  const departments = useMemo(() => {
    return ['all', ...new Set(users.map(u => u.profile?.department).filter(Boolean))];
  }, [users]);

  const userLookup = useMemo(() => {
    const map = new Map();
    users.forEach(u => map.set(u.id, u));
    return map;
  }, [users]);

  const filteredRecipients = users.filter(u => {
    const matchesRole = recipientRole === 'all' ? true : u.role === recipientRole;
    const matchesDept = departmentFilter === 'all' || u.profile?.department === departmentFilter;
    const matchesSearch = searchQuery === '' ||
      u.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesDept && matchesSearch && u.id !== user?.id;
  });

  const filteredFeedbacks = feedbacks.map(fb => {
    const sender = userLookup.get(fb.senderId);
    return { ...fb, sender };
  }).sort((a, b) => b.createdAt - a.createdAt);

  const handleSend = async () => {
    if (!selectedRecipient) {
      toast.error('Select a recipient');
      return;
    }
    if (!sendText.trim()) {
      toast.error('Feedback message is required');
      return;
    }

    const recipient = users.find(u => u.id === selectedRecipient);
    if (!recipient) {
      toast.error('Recipient not found');
      return;
    }

    try {
      await createFeedback({
        senderId: user?.id,
        senderRole: 'admin',
        receiverId: recipient.id,
        receiverRole: recipient.role,
        message: sendText.trim(),
        type: sendType,
        department: recipient.profile?.department || ''
      });

      await createNotification({
        userId: recipient.id,
        type: 'feedback_received',
        title: 'Feedback from Admin',
        message: sendText.slice(0, 80),
        link: recipient.role === 'supervisor' ? '/supervisor/feedback' : '/employee/feedback'
      });

      toast.success('Feedback sent');
      setSendText('');
      setSendType('general');
      setSelectedRecipient('');
      loadFeedbacks();
    } catch (error) {
      toast.error('Failed to send feedback');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
          <p className="text-gray-500 mt-1">Review and send feedback across the organization</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Send className="w-5 h-5 text-teal-500" /> Send Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Recipient Role</Label>
              <Select value={recipientRole} onValueChange={setRecipientRole}>
                <SelectTrigger><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept === 'all' ? 'All Departments' : dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Search Recipient</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Search by name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select Recipient</Label>
              <div className="border rounded-lg max-h-52 overflow-y-auto">
                {filteredRecipients.map(u => (
                  <button
                    key={u.id}
                    onClick={() => setSelectedRecipient(u.id)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b last:border-0 hover:bg-gray-50 ${selectedRecipient === u.id ? 'bg-teal-50' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold">
                      {u.profile?.firstName?.[0]}{u.profile?.lastName?.[0]}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{u.profile?.firstName} {u.profile?.lastName}</p>
                      <p className="text-xs text-gray-500">{u.role} • {u.profile?.department}</p>
                    </div>
                  </button>
                ))}
                {filteredRecipients.length === 0 && (
                  <div className="p-3 text-sm text-gray-500">No matching recipients</div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Feedback Type</Label>
              <Select value={sendType} onValueChange={setSendType}>
                <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="appreciation">Appreciation</SelectItem>
                  <SelectItem value="issue">Issue</SelectItem>
                  <SelectItem value="suggestion">Suggestion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={sendText}
                onChange={(e) => setSendText(e.target.value)}
                placeholder="Share your feedback"
                className="min-h-28"
              />
            </div>

            <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSend} disabled={feedbackLoading}>
              <Send className="w-4 h-4 mr-2" /> {feedbackLoading ? 'Sending...' : 'Send Feedback'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><MessageSquare className="w-5 h-5 text-violet-500" /> Received Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {filteredFeedbacks.length === 0 && (
              <div className="text-center py-10 text-gray-500">No feedback yet</div>
            )}
            {filteredFeedbacks.map(fb => {
              const senderName = fb.anonymous ? 'Anonymous' : `${fb.sender?.profile?.firstName || 'Sender'} ${fb.sender?.profile?.lastName || ''}`;
              const senderDept = fb.sender?.profile?.department;
              return (
                <div key={fb.id} className="p-4 border border-gray-100 rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{senderName}</p>
                      <p className="text-xs text-gray-500">{fb.senderRole}{senderDept ? ` • ${senderDept}` : ''}</p>
                    </div>
                    <Badge variant="secondary" className="capitalize">{fb.type}</Badge>
                  </div>
                  <p className="text-gray-700 mt-2 whitespace-pre-line">{fb.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{new Date(fb.createdAt).toLocaleString()}</p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
